import { Button } from "@/components/ui/button"
import { ExpertBookingCalendar } from "@/features/appointments/components/ExpertBookingCalendar"
import { handleExpertRole } from "@/features/chat/utils/handleExpertRole"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { CalendlyExpert } from "../types/CalendlyExpert"
import { ExpertGender } from "../types/ExpertGender"
import { ExpertGenderRole } from "../types/ExpertGenderRole"

interface Props {
    expert: CalendlyExpert,
    onExpertInfoClick: (expert: CalendlyExpert) => void,
    onContactClick: (expert: CalendlyExpert) => void,
    onBookClick: (expert: CalendlyExpert) => void,
    isExpanded: boolean,
    onExpandChange: (expert: CalendlyExpert) => void,
}

const ExpertCard: React.FC<Props> = ({
    expert,
    onExpertInfoClick,
    onContactClick,
    onBookClick,
    isExpanded,
    onExpandChange,
}) => {

    const handleToggleExpand = () => {
        onExpandChange(expert);
    };

    return (
        <div className="w-full rounded-md flex flex-col items-center justify-center mb-[20px] shadow-platform-new flex-shrink-0 border border-solid border-border">
            <div className="w-full h-full p-[20px] flex flex-col md:flex-row md:justify-between">
                <ExpertSection
                    expert={expert}
                    onContactClick={onContactClick}
                />
                <ButtonsSection
                    expert={expert}
                    onExpertInfoClick={onExpertInfoClick}
                    isExpanded={isExpanded}
                    onToggleExpand={handleToggleExpand}
                />
            </div>
            {isExpanded && (
                <ExpertBookingCalendar
                    expert={expert}
                    onBookClick={onBookClick}
                />
            )}
        </div>
    )
}

export default ExpertCard

const ExpertSection: React.FC<{
    expert: CalendlyExpert,
    onContactClick: (expert: CalendlyExpert) => void,
}> = ({
    expert,
    onContactClick,
}) => {
        const navigate = useNavigate();
        const { calendly_data, data } = expert;
        const avatarUrl = calendly_data.calendly_user.avatar_url;
        const fullName = `${data.name} ${data.family_name}`;

        return (
            <div className="flex items-center justify-between mb-[20px] md:mb-0">
                <div className="w-[93px] h-[93px] rounded-full border border-solid border-border flex items-center justify-center md:h-full aspect-square overflow-hidden">
                    <img
                        className="w-full h-full object-cover"
                        src={avatarUrl}
                        alt={`Avatar of ${fullName}`}
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <div className="w-[calc(100%-110px)] h-[85px] flex flex-col justify-between md:w-fit md:ml-[20px]">
                    <div className={`w-full flex items-center cursor-pointer space-x-2.5 justify-end md:justify-start`}>
                        <button
                            onClick={() => navigate("/chat")}
                            className="flex items-center gap-2 hover:text-foreground-50 transition-colors md:mr-[20px]"
                        >
                            <span className="font-bold text-[16px] leading-[22px]">
                                Scrivi
                            </span>
                            <svg width={21} height={20} viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M10.0282 0C8.71497 0 7.41461 0.258658 6.20135 0.761205C4.9881 1.26375 3.8857 2.00035 2.95712 2.92893C1.08175 4.8043 0.0281864 7.34784 0.0281864 10C0.0194442 12.3091 0.818979 14.5485 2.28819 16.33L0.288186 18.33C0.149429 18.4706 0.0554325 18.6492 0.0180584 18.8432C-0.0193158 19.0372 0.0016069 19.2379 0.0781863 19.42C0.161244 19.5999 0.29589 19.7511 0.465033 19.8544C0.634176 19.9577 0.830187 20.0083 1.02819 20H10.0282C12.6804 20 15.2239 18.9464 17.0993 17.0711C18.9746 15.1957 20.0282 12.6522 20.0282 10C20.0282 7.34784 18.9746 4.8043 17.0993 2.92893C15.2239 1.05357 12.6804 0 10.0282 0ZM10.0282 18H3.43819L4.36819 17.07C4.55444 16.8826 4.65898 16.6292 4.65898 16.365C4.65898 16.1008 4.55444 15.8474 4.36819 15.66C3.05877 14.352 2.24336 12.6305 2.06088 10.7888C1.87839 8.94705 2.34013 7.09901 3.36741 5.55952C4.3947 4.02004 5.92398 2.88436 7.6947 2.34597C9.46543 1.80759 11.368 1.8998 13.0784 2.60691C14.7888 3.31402 16.201 4.59227 17.0746 6.22389C17.9482 7.85551 18.2291 9.73954 17.8693 11.555C17.5096 13.3705 16.5315 15.005 15.1017 16.1802C13.672 17.3554 11.8789 17.9985 10.0282 18Z"
                                    fill={"black"}
                                />
                            </svg>
                        </button>
                    </div>
                    <div>
                        <div className="font-bold text-[12px] leading-[20px]">{expert.data.sex === ExpertGender.Female ? ExpertGenderRole.Dottssa : ExpertGenderRole.Dott}</div>
                        <div className="font-bold text-[20px] leading-[20px]">{expert.data.name} {expert.data.family_name}</div>
                    </div>
                    <div className="font-bold text-[14px] leading-[20px] text-holifya-blue">{handleExpertRole(expert)}</div>
                </div>
            </div>
        )
    }

const ButtonsSection: React.FC<{
    expert: CalendlyExpert
    onExpertInfoClick: (expert: CalendlyExpert) => void
    isExpanded: boolean
    onToggleExpand: () => void
}> = ({
    expert,
    onExpertInfoClick,
    isExpanded,
    onToggleExpand,
}) => {
        return (
            <div className="flex flex-col items-end justify-between space-y-2">
                {/*  */}
                <Button
                    size={'form'}
                    variant={'white'}
                    className="normal-case text-sm"
                    onClick={() => onExpertInfoClick(expert)}
                >
                    Conosci meglio {expert.data.name}
                </Button>
                <Button
                    size="form"
                    onClick={onToggleExpand}
                    className="flex items-center font-bold text-white md:p-5"
                >
                    {isExpanded ? (
                        <>
                            <span className="mr-0.5">Chiudi calendario</span>
                            <ChevronUp className="w-5 h-5" />
                        </>
                    ) : (
                        <>
                            <span className="mr-0.5">Prenota consulto</span>
                            <ChevronDown className="w-5 h-5" />
                        </>
                    )}
                </Button>
            </div>
        )
    }
